import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { syncEngine } from '@/lib/offline/sync';
import { Product, Category } from './useProducts';

// ============================================
// TYPES
// ============================================

export interface UpsellSuggestion {
  id: string;
  product_id: string;
  product_name: string;
  product_price: number;
  product_image_url: string | null;
  category_id: string | null;
  category_name: string | null;
  association_type: 'manual' | 'computed' | 'ai' | 'heuristic';
  score: number;
  prompt_text: string | null;
}

export interface ProductUpsell {
  id: string;
  location_id: string;
  source_product_id: string;
  target_product_id: string;
  association_type: 'manual' | 'computed' | 'ai';
  score: number;
  co_purchase_count: number;
  is_active: boolean;
  priority: number;
  prompt_text: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpsellAnalytics {
  source_product_id: string;
  source_product_name: string;
  suggested_product_id: string;
  suggested_product_name: string;
  shown_count: number;
  accepted_count: number;
  conversion_rate: number;
  last_shown_at: string | null;
  last_accepted_at: string | null;
}

export interface CreateUpsellInput {
  source_product_id: string;
  target_product_id: string;
  priority?: number;
  prompt_text?: string;
}

// ============================================
// HEURISTIC RULES
// ============================================

// Category pairing rules for smart heuristics
// Maps category keywords to complementary category keywords
const CATEGORY_PAIRINGS: Record<string, string[]> = {
  // Food categories
  'entree': ['beverage', 'drink', 'side', 'dessert', 'appetizer'],
  'main': ['beverage', 'drink', 'side', 'dessert', 'appetizer'],
  'meal': ['beverage', 'drink', 'side', 'dessert'],
  'burger': ['fries', 'side', 'beverage', 'drink', 'shake'],
  'sandwich': ['side', 'beverage', 'drink', 'chips', 'soup'],
  'pizza': ['beverage', 'drink', 'side', 'wings', 'breadstick'],
  'breakfast': ['coffee', 'juice', 'beverage', 'side'],
  'porridge': ['bread', 'roll', 'pastry', 'coffee', 'juice'],
  'cereal': ['milk', 'juice', 'coffee', 'pastry'],
  'soup': ['bread', 'roll', 'salad', 'sandwich'],
  'salad': ['soup', 'bread', 'beverage', 'drink'],
  'appetizer': ['entree', 'main', 'beverage', 'drink'],
  'starter': ['entree', 'main', 'beverage', 'drink'],
  // Beverages
  'beverage': ['side', 'dessert', 'snack'],
  'drink': ['side', 'dessert', 'snack'],
  'coffee': ['pastry', 'dessert', 'breakfast', 'muffin', 'croissant'],
  'tea': ['pastry', 'dessert', 'sandwich'],
  // Desserts
  'dessert': ['coffee', 'tea', 'beverage'],
  'ice cream': ['topping', 'beverage'],
  // Retail
  'electronics': ['accessory', 'cable', 'case', 'warranty'],
  'phone': ['case', 'charger', 'screen protector', 'earbuds'],
  'laptop': ['bag', 'mouse', 'keyboard', 'warranty'],
};

// Generic prompts based on category type
const CATEGORY_PROMPTS: Record<string, string> = {
  'beverage': 'Would you like a drink with that?',
  'drink': 'Would you like a drink with that?',
  'side': 'Add a side to complete your meal?',
  'dessert': 'Save room for dessert?',
  'coffee': 'How about a coffee?',
  'appetizer': 'Start with an appetizer?',
};

// ============================================
// HEURISTIC SUGGESTION LOGIC
// ============================================

function findComplementaryCategories(categoryName: string | null): string[] {
  if (!categoryName) return [];
  
  const lowerName = categoryName.toLowerCase();
  
  // Find matching pairing rules
  for (const [key, pairings] of Object.entries(CATEGORY_PAIRINGS)) {
    if (lowerName.includes(key)) {
      return pairings;
    }
  }
  
  // Default: suggest beverages and sides for any food item
  return ['beverage', 'drink', 'side'];
}

function getHeuristicPrompt(categoryName: string | null): string | null {
  if (!categoryName) return null;
  
  const lowerName = categoryName.toLowerCase();
  
  for (const [key, prompt] of Object.entries(CATEGORY_PROMPTS)) {
    if (lowerName.includes(key)) {
      return prompt;
    }
  }
  
  return null;
}

function generateHeuristicSuggestions(
  sourceProduct: { id: string; category_id: string | null; price: number; effective_price?: number },
  sourceCategory: Category | null,
  allProducts: Product[],
  allCategories: Category[],
  excludeProductIds: string[],
  limit: number = 3
): UpsellSuggestion[] {
  const complementaryKeywords = findComplementaryCategories(sourceCategory?.name || null);
  
  if (complementaryKeywords.length === 0) return [];
  
  // Build a map of category names to categories
  const categoryMap = new Map<string, Category>();
  allCategories.forEach(cat => categoryMap.set(cat.id, cat));
  
  // Find products in complementary categories
  const suggestions: UpsellSuggestion[] = [];
  
  for (const product of allProducts) {
    // Skip if excluded or same as source
    if (excludeProductIds.includes(product.id) || product.id === sourceProduct.id) continue;
    
    // Skip unavailable products
    if (!product.is_active || !product.is_available) continue;
    
    // Get product's category
    const productCategory = product.category_id ? categoryMap.get(product.category_id) : null;
    if (!productCategory) continue;
    
    const categoryNameLower = productCategory.name.toLowerCase();
    
    // Check if this category matches any complementary keywords
    const matchingKeyword = complementaryKeywords.find(keyword => 
      categoryNameLower.includes(keyword)
    );
    
    if (matchingKeyword) {
      // Prefer lower-priced items for upsells (affordable add-ons)
      const productPrice = product.effective_price ?? product.price;
      const sourcePrice = sourceProduct.effective_price ?? sourceProduct.price;
      const priceScore = productPrice <= sourcePrice * 0.5 ? 70 :
                         productPrice <= sourcePrice ? 50 : 30;

      suggestions.push({
        id: `heuristic-${product.id}`,
        product_id: product.id,
        product_name: product.name,
        product_price: productPrice,
        product_image_url: product.image_url,
        category_id: product.category_id,
        category_name: productCategory.name,
        association_type: 'heuristic',
        score: priceScore,
        prompt_text: getHeuristicPrompt(productCategory.name),
      });
    }
  }
  
  // Sort by score and return top suggestions
  return suggestions
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

// ============================================
// HOOK
// ============================================

export function useUpsellSuggestions() {
  const queryClient = useQueryClient();
  const { currentLocation } = useLocationStore();
  const locationId = currentLocation?.id;

  // Fetch all manual upsell rules for this location
  const {
    data: upsellRules,
    isLoading: rulesLoading,
    refetch: refetchRules,
  } = useQuery({
    queryKey: ['upsell-rules', locationId],
    queryFn: async () => {
      if (!locationId) return [];

      const { data, error } = await supabase
        .from('product_upsells')
        .select(`
          *,
          source_product:products!product_upsells_source_product_id_fkey(id, name, price, image_url, category_id),
          target_product:products!product_upsells_target_product_id_fkey(id, name, price, image_url, category_id)
        `)
        .eq('location_id', locationId)
        .eq('is_active', true)
        .order('priority', { ascending: false });

      if (error) throw error;
      return data as (ProductUpsell & {
        source_product: Product;
        target_product: Product;
      })[];
    },
    enabled: !!locationId,
  });

  // Get suggestions for a specific product
  const getSuggestions = useCallback(async (
    productId: string,
    excludeProductIds: string[] = [],
    allProducts: Product[] = [],
    allCategories: Category[] = [],
    limit: number = 3
  ): Promise<UpsellSuggestion[]> => {
    if (!locationId) return [];

    // Check if online before making network request
    const isOnline = syncEngine.state.isOnline;

    // Only try database suggestions if online
    if (isOnline) {
      try {
        const { data: dbSuggestions, error } = await supabase
          .rpc('get_upsell_suggestions', {
            p_location_id: locationId,
            p_product_id: productId,
            p_exclude_product_ids: excludeProductIds,
            p_limit: limit,
          });

        if (!error && dbSuggestions && dbSuggestions.length > 0) {
          return dbSuggestions as UpsellSuggestion[];
        }
      } catch (e) {
        // Silently fall through to heuristics when offline or on error
        console.debug('[Upsell] RPC failed, using heuristics:', e);
      }
    }

    // Fall back to heuristic suggestions (works offline)
    const sourceProduct = allProducts.find(p => p.id === productId);
    if (!sourceProduct) return [];

    const sourceCategory = sourceProduct.category_id
      ? allCategories.find(c => c.id === sourceProduct.category_id) || null
      : null;

    return generateHeuristicSuggestions(
      sourceProduct,
      sourceCategory,
      allProducts,
      allCategories,
      [...excludeProductIds, productId],
      limit
    );
  }, [locationId]);

  // Track when a suggestion is shown (silent - doesn't throw on error)
  const trackShown = useMutation({
    mutationFn: async ({
      sourceProductId,
      suggestedProductId,
    }: {
      sourceProductId: string;
      suggestedProductId: string;
    }) => {
      if (!locationId) return;

      try {
        await supabase.rpc('track_upsell_shown', {
          p_location_id: locationId,
          p_source_product_id: sourceProductId,
          p_suggested_product_id: suggestedProductId,
        });
      } catch (e) {
        // Silently ignore - function may not exist yet
        console.debug('Upsell tracking not available:', e);
      }
    },
  });

  // Track when a suggestion is accepted (silent - doesn't throw on error)
  const trackAccepted = useMutation({
    mutationFn: async ({
      sourceProductId,
      suggestedProductId,
    }: {
      sourceProductId: string;
      suggestedProductId: string;
    }) => {
      if (!locationId) return;

      try {
        await supabase.rpc('track_upsell_accepted', {
          p_location_id: locationId,
          p_source_product_id: sourceProductId,
          p_suggested_product_id: suggestedProductId,
        });
      } catch (e) {
        // Silently ignore - function may not exist yet
        console.debug('Upsell tracking not available:', e);
      }
    },
  });

  // Create a manual upsell rule
  const createUpsell = useMutation({
    mutationFn: async (input: CreateUpsellInput) => {
      if (!locationId) throw new Error('No location selected');

      const { data: user } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('product_upsells')
        .insert({
          location_id: locationId,
          source_product_id: input.source_product_id,
          target_product_id: input.target_product_id,
          association_type: 'manual',
          score: 100, // Manual rules get highest score
          priority: input.priority ?? 0,
          prompt_text: input.prompt_text,
          is_active: true,
          created_by: user.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upsell-rules', locationId] });
    },
  });

  // Update an upsell rule
  const updateUpsell = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Pick<ProductUpsell, 'priority' | 'prompt_text' | 'is_active'>>;
    }) => {
      const { data, error } = await supabase
        .from('product_upsells')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upsell-rules', locationId] });
    },
  });

  // Delete an upsell rule
  const deleteUpsell = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('product_upsells')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upsell-rules', locationId] });
    },
  });

  // Compute associations from order history
  const computeAssociations = useMutation({
    mutationFn: async (daysBack: number = 90) => {
      if (!locationId) throw new Error('No location selected');

      const { data, error } = await supabase.rpc('compute_product_associations', {
        p_location_id: locationId,
        p_days_back: daysBack,
        p_min_co_purchase_count: 3,
      });

      if (error) throw error;
      return data as number; // Returns count of associations created
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upsell-rules', locationId] });
    },
  });

  // Fetch analytics
  const {
    data: analytics,
    isLoading: analyticsLoading,
    refetch: refetchAnalytics,
  } = useQuery({
    queryKey: ['upsell-analytics', locationId],
    queryFn: async () => {
      if (!locationId) return [];

      const { data, error } = await supabase.rpc('get_upsell_analytics', {
        p_location_id: locationId,
      });

      if (error) throw error;
      return data as UpsellAnalytics[];
    },
    enabled: !!locationId,
  });

  // Get rules grouped by source product
  const rulesByProduct = useMemo(() => {
    const grouped: Record<string, typeof upsellRules> = {};
    
    upsellRules?.forEach(rule => {
      if (!grouped[rule.source_product_id]) {
        grouped[rule.source_product_id] = [];
      }
      grouped[rule.source_product_id]!.push(rule);
    });
    
    return grouped;
  }, [upsellRules]);

  return {
    // Data
    upsellRules: upsellRules ?? [],
    rulesByProduct,
    analytics: analytics ?? [],
    
    // Loading states
    isLoading: rulesLoading,
    analyticsLoading,
    
    // Actions
    getSuggestions,
    trackShown,
    trackAccepted,
    createUpsell,
    updateUpsell,
    deleteUpsell,
    computeAssociations,
    
    // Refetch
    refetchRules,
    refetchAnalytics,
  };
}

